import * as React from "react";

import { useEphemeralWorkspaceState } from "@/shared/components/visualization/WorkspaceCtx";
import { MessagePathPartType, TopicRecord } from "@/shared/domain/topics";

import type { TopicDataPathBinding } from "./positionTopicData";
import { PositionPreset, presets } from "./presets";

/**
 * Presets bound to the PX4 position topics carried by the workspace's open
 * files, one per matching topic, so a user can pick a working position without
 * naming a path for each axis.
 */
export function useWorkspacePositionPresets() {
  const state = useEphemeralWorkspaceState();
  const prefilledPresets = React.useMemo(
    // TODO(dave): we can pull this out into a hook, and use it to handle topic drag-and-drop.
    // Any dropped topic that matches a preset would be loaded.
    function findValidPresets() {
      const prefilled: PositionPreset[] = [];
      const idToFile = Object.fromEntries(
        state.files.map((f) => [f.file_id, f]),
      );
      for (const topic of state.topics) {
        const file = idToFile[topic.association.association_id];
        if (topic.topic_name === "vehicle_global_position") {
          prefilled.push(
            specifyPreset({
              preset: presets.lat_lon_alt,
              title:
                state.files.length > 1
                  ? `vehicle_global_position (PX4) (${file.name})`
                  : "vehicle_global_position (PX4)",
              fileId: file.file_id,
              topic,
              rightAttribute: "lat",
              forwardAttribute: "lon",
              upAttribute: "alt_ellipsoid",
            }),
          );
        }
        if (topic.topic_name === "vehicle_local_position") {
          prefilled.push(
            specifyPreset({
              preset: presets.north_east_down,
              title:
                state.files.length > 1
                  ? `vehicle_local_position (PX4) (${file.name})`
                  : "vehicle_local_position (PX4)",
              fileId: file.file_id,
              topic,
              forwardAttribute: "x",
              rightAttribute: "y",
              upAttribute: "z",
            }),
          );
        }
      }
      return prefilled;
    },
    [state.files, state.topics],
  );
  return prefilledPresets;
}

/**
 * A copy of a coordinate-convention preset with each axis bound to a field of
 * one topic.
 */
function specifyPreset({
  preset,
  title,
  fileId,
  topic,
  forwardAttribute,
  rightAttribute,
  upAttribute,
}: {
  preset: PositionPreset;
  title: string;
  fileId: string;
  topic: TopicRecord;
  forwardAttribute: string;
  rightAttribute: string;
  upAttribute: string;
}): PositionPreset {
  return {
    ...preset,
    name: {
      // A hint at the file formats that use the convention is redundant once
      // the preset names a topic from a file already open.
      formatHint: "",
      subtitle: `${preset.name.title} (${preset.name.subtitle})`,
      title,
    },
    bindings: {
      forward: bindingForAttribute(topic, fileId, forwardAttribute),
      right: bindingForAttribute(topic, fileId, rightAttribute),
      up: bindingForAttribute(topic, fileId, upAttribute),
    },
  };
}

/**
 * Binds one axis to a top-level field of a topic, or undefined when the topic
 * declares no field of that name. A preset is picked by topic name alone, so a
 * file can carry the topic without the field an axis wants.
 */
function bindingForAttribute(
  topic: TopicRecord,
  fileId: string,
  attribute: string,
): TopicDataPathBinding | undefined {
  const messagePath = topic.message_paths.find(
    (candidate) => candidate.message_path === attribute,
  );
  if (messagePath === undefined) {
    return undefined;
  }

  return {
    fileId,
    topicName: topic.topic_name,
    messagePath: [
      {
        type: MessagePathPartType.Attr,
        attribute,
        dataType: messagePath.canonical_data_type,
      },
    ],
  };
}
